// client/src/utils/outbox.js
// Generic IndexedDB-backed outbox for actions taken while offline. Records are
// replayed when connectivity returns. Kept type-agnostic so it can back the
// message outbox today and other write types later.

const DB_NAME = 'ameca-offline';
const DB_VERSION = 1;
const STORE = 'outbox';

function hasIndexedDB() {
  try {
    return typeof indexedDB !== 'undefined' && indexedDB !== null;
  } catch (_) {
    return false;
  }
}

function openDB() {
  return new Promise((resolve, reject) => {
    if (!hasIndexedDB()) {
      reject(new Error('IndexedDB not available'));
      return;
    }
    const req = indexedDB.open(DB_NAME, DB_VERSION);
    req.onupgradeneeded = () => {
      const db = req.result;
      if (!db.objectStoreNames.contains(STORE)) {
        db.createObjectStore(STORE, { keyPath: 'id', autoIncrement: true });
      }
    };
    req.onsuccess = () => resolve(req.result);
    req.onerror = () => reject(req.error || new Error('Failed to open IndexedDB'));
  });
}

export function isSupported() {
  return hasIndexedDB();
}

// Add a record; resolves with the stored record (including its generated id).
export async function enqueue(record) {
  const db = await openDB();
  try {
    return await new Promise((resolve, reject) => {
      const tx = db.transaction(STORE, 'readwrite');
      const store = tx.objectStore(STORE);
      const toStore = { tries: 0, createdAt: Date.now(), ...record };
      const req = store.add(toStore);
      req.onsuccess = () => resolve({ ...toStore, id: req.result });
      req.onerror = () => reject(req.error);
    });
  } finally {
    db.close();
  }
}

// List all records, optionally filtered by `type`, oldest first.
export async function getAll(type) {
  const db = await openDB();
  try {
    return await new Promise((resolve, reject) => {
      const tx = db.transaction(STORE, 'readonly');
      const req = tx.objectStore(STORE).getAll();
      req.onsuccess = () => {
        let rows = req.result || [];
        if (type) rows = rows.filter((r) => r.type === type);
        rows.sort((a, b) => (a.createdAt || 0) - (b.createdAt || 0));
        resolve(rows);
      };
      req.onerror = () => reject(req.error);
    });
  } finally {
    db.close();
  }
}

export async function update(record) {
  const db = await openDB();
  try {
    return await new Promise((resolve, reject) => {
      const tx = db.transaction(STORE, 'readwrite');
      const req = tx.objectStore(STORE).put(record);
      req.onsuccess = () => resolve(record);
      req.onerror = () => reject(req.error);
    });
  } finally {
    db.close();
  }
}

export async function remove(id) {
  const db = await openDB();
  try {
    return await new Promise((resolve, reject) => {
      const tx = db.transaction(STORE, 'readwrite');
      const req = tx.objectStore(STORE).delete(id);
      req.onsuccess = () => resolve();
      req.onerror = () => reject(req.error);
    });
  } finally {
    db.close();
  }
}
